import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer,
} from 'recharts'
import {
  getMetrics, getMilestones, getProjects, getSentWeeks,
  getTeamMembers, getAllocations, getSentWeekAllocations, getTasks,
} from '../api/client'
import { useAuth } from '../context/AuthContext'
import { formatDate, toISODate, currentWeekMonday, formatWeekRange } from '../utils/weeks'
import PageHeader, { Ico, StatCardModern, TodayPill } from '../components/PageHeader'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fridayOf(mondayStr) {
  const d = new Date(mondayStr + 'T00:00:00')
  d.setDate(d.getDate() + 4)
  return toISODate(d)
}

function isLightHex(hex) {
  if (!hex || typeof hex !== 'string') return false
  const h = hex.replace('#', '')
  if (h.length !== 6) return false
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 155
}

function isOverdue(d) {
  if (!d) return false
  return new Date(d + 'T00:00:00') < new Date().setHours(0, 0, 0, 0)
}
function isSoon(d) {
  if (!d) return false
  const diff = (new Date(d + 'T00:00:00') - new Date().setHours(0, 0, 0, 0)) / 86400000
  return diff >= 0 && diff <= 7
}

// ── Shared sub-components ─────────────────────────────────────────────────────

const TYPE_LABELS = { PTO: 'PTO', medical: 'Medical Leave', work_trip: 'Work Trip', admin: 'Administrative Leave' }
const TYPE_COLORS = {
  PTO: 'bg-emerald-100 text-emerald-700',
  medical: 'bg-red-100 text-red-700',
  work_trip: 'bg-blue-100 text-blue-700',
  admin: 'bg-amber-100 text-amber-700',
}
const TYPE_DOT = { PTO: 'bg-emerald-400', medical: 'bg-red-400', work_trip: 'bg-blue-400', admin: 'bg-amber-400' }

const STATUS_STYLES = {
  todo:        'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  done:        'bg-emerald-100 text-emerald-700',
}
const PRIORITY_STYLES = {
  low:    'bg-gray-100 text-gray-500',
  medium: 'bg-amber-100 text-amber-700',
  high:   'bg-red-100 text-red-600',
}



function MilestoneTimeline({ milestones, projectMap }) {
  const { t } = useTranslation('engineering')
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const withDates = milestones
    .filter(m => m.due_date)
    .map(m => ({ ...m, dateObj: new Date(m.due_date + 'T00:00:00') }))
    .sort((a, b) => a.dateObj - b.dateObj)

  const overdue   = withDates.filter(m => !m.completed && m.dateObj < today)
  const thisWeek  = withDates.filter(m => !m.completed && m.dateObj >= today && (m.dateObj - today) / 86400000 <= 6)
  const twoWeeks  = withDates.filter(m => !m.completed && (m.dateObj - today) / 86400000 > 6 && (m.dateObj - today) / 86400000 <= 14)
  const thisMonth = withDates.filter(m => !m.completed && (m.dateObj - today) / 86400000 > 14 && (m.dateObj - today) / 86400000 <= 45)
  const later     = withDates.filter(m => !m.completed && (m.dateObj - today) / 86400000 > 45)

  const groups = [
    { label: t('dashboard.milestone.overdue'),     items: overdue,   dotColor: 'bg-red-500',   labelColor: 'text-red-600',   bg: 'bg-red-50' },
    { label: t('dashboard.milestone.thisWeek'),    items: thisWeek,  dotColor: 'bg-amber-400', labelColor: 'text-amber-700', bg: 'bg-amber-50' },
    { label: t('dashboard.milestone.next2Weeks'),  items: twoWeeks,  dotColor: 'bg-gray-400',  labelColor: 'text-gray-700',  bg: 'bg-gray-50' },
    { label: t('dashboard.milestone.thisMonth'),   items: thisMonth, dotColor: 'bg-gray-400',  labelColor: 'text-gray-600',  bg: '' },
    { label: t('dashboard.milestone.later'),       items: later,     dotColor: 'bg-gray-300',  labelColor: 'text-gray-400',  bg: '' },
  ].filter(g => g.items.length > 0)

  if (groups.length === 0) return <p className="text-gray-400 text-sm text-center py-8">{ t('dashboard.milestone.noUpcoming')}</p>

  return (
    <div className="space-y-5">
      {groups.map(group => (
        <div key={group.label}>
          <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${group.labelColor}`}>{group.label}</p>
          <div className={`space-y-1 rounded-xl ${group.bg} ${group.bg ? 'p-3' : ''}`}>
            {group.items.map(ms => {
              const proj = projectMap[ms.project_id]
              return (
                <div key={ms.id} className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${group.dotColor}`} />
                  {proj && <div className="w-1.5 h-4 rounded-full shrink-0" style={{ backgroundColor: proj.color }} />}
                  <span className="flex-1 text-sm text-gray-800 truncate">{ms.name}</span>
                  {proj && <span className="text-xs text-gray-400 shrink-0 max-w-28 truncate">{proj.nickname || proj.name}</span>}
                  <span className="text-xs font-medium shrink-0 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    {formatDate(ms.due_date)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── My Week tab ───────────────────────────────────────────────────────────────

function MyWeekTab({ userName, teamMemberId }) {
  const { t: tl } = useTranslation('engineering')
  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return tl('dashboard.myWeek.greetingMorning')
    if (h < 18) return tl('dashboard.myWeek.greetingAfternoon')
    return tl('dashboard.myWeek.greetingEvening')
  }
  const [loading, setLoading] = useState(true)
  const [week, setWeek] = useState(null)
  const [myAllocs, setMyAllocs] = useState([])
  const [myTasks, setMyTasks] = useState([])
  const [myMilestones, setMyMilestones] = useState([])
  const [projectMap, setProjectMap] = useState({})

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [sentWeeks, projects, milestones, tasks] = await Promise.all([
          getSentWeeks(),
          getProjects(),
          getMilestones(),
          getTasks(),
        ])
        if (cancelled) return

        // Resolve week
        const sorted = Array.isArray(sentWeeks)
          ? [...sentWeeks].sort((a, b) => b.week_start.localeCompare(a.week_start))
          : []
        const isSent = sorted.length > 0
        const resolvedWeek = isSent ? sorted[0].week_start : currentWeekMonday()
        setWeek(resolvedWeek)

        // Allocations for last sent week
        const allocs = isSent
          ? await getSentWeekAllocations(resolvedWeek)
          : await getAllocations({ week_start: resolvedWeek })
        if (cancelled) return

        const pMap = Object.fromEntries((Array.isArray(projects) ? projects : []).map(p => [p.id, p]))
        setProjectMap(pMap)

        // Filter to this user — prefer team_member_id (FK), fall back to name for sent allocs
        const userAllocs = (Array.isArray(allocs) ? allocs : [])
          .filter(a => {
            if (a.hours <= 0) return false
            if (teamMemberId) return a.team_member_id === teamMemberId
            // name-based fallback only works for sent week allocations which carry team_member_name
            return a.team_member_name != null && a.team_member_name === userName
          })
          .sort((a, b) => b.hours - a.hours)
        setMyAllocs(userAllocs)

        const myProjectIds = new Set(userAllocs.map(a => a.project_id))

        // Filter tasks — prefer responsible_id (FK), fall back to name
        const activeTasks = (Array.isArray(tasks) ? tasks : [])
          .filter(t => {
            if (t.status === 'done') return false
            if (teamMemberId) return t.responsible_id === teamMemberId
            return t.responsible_name != null && t.responsible_name === userName
          })
        setMyTasks(activeTasks)

        // Filter milestones to user's projects
        setMyMilestones(
          (Array.isArray(milestones) ? milestones : [])
            .filter(m => myProjectIds.has(m.project_id) && !m.completed && m.due_date)
        )
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [userName, teamMemberId])

  if (loading) return <div className="text-sm text-gray-400">Loading…</div>

  const totalHours = myAllocs.reduce((s, a) => s + a.hours, 0)
  const openTasks  = myTasks.length
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const upcomingSoon = myMilestones.filter(m => {
    const d = new Date(m.due_date + 'T00:00:00')
    return d >= today && (d - today) / 86400000 <= 14
  }).length

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${greeting()} ${userName?.split(' ')[0] ?? ''}!`}
        subtitle={tl('dashboard.myWeek.subtitle')}
        actions={<>
          <TodayPill />
          <Link to="/projects" className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 inline-flex items-center gap-1.5 transition-colors">
            {tl('dashboard.myWeek.viewProjects')} <Ico.ArrowRight />
          </Link>
        </>}
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCardModern
          icon={<Ico.Hours />} iconBg="bg-blue-50" iconColor="text-blue-600"
          label={tl('dashboard.myWeek.myHours')} value={totalHours}
          sub={week ? `${tl('dashboard.myWeek.allocated')} · ${formatWeekRange(week)}` : tl('dashboard.myWeek.allocated')}
        />
        <StatCardModern
          icon={<Ico.Tasks />} iconBg="bg-amber-50" iconColor="text-amber-600"
          label={tl('dashboard.myWeek.openTasks')} value={openTasks}
          sub={openTasks === 0 ? tl('dashboard.myWeek.allCaughtUp') : tl('dashboard.myWeek.assignedToMe')}
        />
        <StatCardModern
          icon={<Ico.Flag />} iconBg="bg-emerald-50" iconColor="text-emerald-600"
          label={tl('dashboard.myWeek.upcomingMilestones')} value={upcomingSoon}
          sub={tl('dashboard.myWeek.inNext14Days')}
        />
        <StatCardModern
          icon={<Ico.Folder />} iconBg="bg-violet-50" iconColor="text-violet-600"
          label={tl('dashboard.myWeek.myProjects')} value={myAllocs.length}
          sub={tl('dashboard.myWeek.activeThisWeek')}
        />
      </div>

      {/* My Tasks table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">{tl('dashboard.myWeek.myTasksHeader')}</h3>
          <span className="text-xs text-gray-400">{tl('dashboard.myWeek.openCount', {count: myTasks.length})}</span>
        </div>
        {myTasks.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">{tl('dashboard.myWeek.noOpenTasks')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{tl('dashboard.myWeek.tableTask')}</th>
                  <th className="text-left px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{tl('dashboard.myWeek.tableProject')}</th>
                  <th className="text-left px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{tl('dashboard.myWeek.tablePriority')}</th>
                  <th className="text-left px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{tl('dashboard.myWeek.tableDeadline')}</th>
                  <th className="text-left px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{tl('dashboard.myWeek.tableStatus')}</th>
                  <th className="text-right px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{tl('dashboard.myWeek.tableAction')}</th>
                </tr>
              </thead>
              <tbody>
                {myTasks.map(t => {
                  const proj = projectMap[t.project_id]
                  return (
                    <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors last:border-b-0">
                      <td className="px-5 py-3 text-sm text-gray-900 max-w-[16rem] truncate" title={t.name}>{t.name}</td>
                      <td className="px-5 py-3 text-sm">
                        {proj ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: proj.color }} />
                            <span className="text-gray-700 truncate">{proj.nickname || proj.name}</span>
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${PRIORITY_STYLES[t.priority] || 'bg-gray-100 text-gray-500'}`}>
                          {t.priority}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {t.deadline ? (
                          <span className={`text-xs ${
                            isOverdue(t.deadline) ? 'text-red-600 font-medium' :
                            isSoon(t.deadline)    ? 'text-amber-600 font-medium' :
                            'text-gray-500'
                          }`}>{formatDate(t.deadline)}</span>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${STATUS_STYLES[t.status]}`}>
                          {t.status === 'in_progress' ? tl('dashboard.status.inProgress') : t.status === 'todo' ? tl('dashboard.status.todo') : tl('dashboard.status.done')}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        {proj && (
                          <Link to={`/projects/${proj.id}`} className="text-xs font-medium text-gray-700 hover:text-gray-900 hover:underline">
                            {tl('dashboard.myWeek.tableView')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* My Projects cards */}
      {myAllocs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">{tl('dashboard.myWeek.myProjectsThisWeek')}</h3>
            <Link to="/projects" className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">
              {tl('dashboard.myWeek.viewAll')} <Ico.ArrowRight />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4">
            {myAllocs.map(a => {
              const proj = projectMap[a.project_id]
              const color = a.project_color || proj?.color || '#6366f1'
              const name  = a.project_name  || proj?.nickname || proj?.name || `Project ${a.project_id}`
              return (
                <Link key={a.project_id} to={`/projects/${a.project_id}`}
                  className="group block bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <span className="w-2 h-2 mt-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <span className="text-[11px] font-semibold text-gray-700 tabular-nums shrink-0">
                      {a.hours}h
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 truncate">{name}</h4>
                  <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                    {proj?.po_pm ? `PM · ${proj.po_pm}` : 'Allocated this week'}
                  </p>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* My Milestones */}
      {myMilestones.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-1">{tl('dashboard.myWeek.milestonesFromMyProjects')}</h3>
          <p className="text-xs text-gray-500 mb-4">{tl('dashboard.myWeek.upcomingDates')}</p>
          <MilestoneTimeline milestones={myMilestones} projectMap={projectMap} />
        </div>
      )}
    </div>
  )
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab() {
  const { t } = useTranslation('engineering')
  const today = new Date()
  const firstOfMonth = toISODate(new Date(today.getFullYear(), today.getMonth(), 1))
  const todayStr = toISODate(today)

  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate]     = useState(null)
  const [datesReady, setDatesReady] = useState(false)
  const [metrics, setMetrics]       = useState(null)
  const [allMilestones, setAllMilestones] = useState([])
  const [allProjects, setAllProjects]     = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let monday = null
      try {
        const sent = await getSentWeeks()
        if (Array.isArray(sent) && sent.length > 0) {
          const sorted = [...sent].sort((a, b) => b.week_start.localeCompare(a.week_start))
          monday = sorted[0]?.week_start ?? null
        }
      } catch {}
      if (cancelled) return
      setStartDate(monday ? monday : firstOfMonth)
      setEndDate(monday ? fridayOf(monday) : todayStr)
      setDatesReady(true)
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!datesReady || !startDate || !endDate) return
    setLoading(true)
    Promise.all([
      getMetrics({ start_date: startDate, end_date: endDate }),
      getMilestones(),
      getProjects(),
    ]).then(([data, milestones, projects]) => {
      if (data && typeof data.total_hours === 'number') setMetrics(data)
      if (Array.isArray(milestones)) setAllMilestones(milestones)
      if (Array.isArray(projects)) setAllProjects(projects.filter(p => !p.is_purchase_only))
    }).catch(() => {}).finally(() => setLoading(false))
  }, [startDate, endDate, datesReady])

  const safeProjects   = Array.isArray(allProjects) ? allProjects : []
  const safeMilestones = Array.isArray(allMilestones) ? allMilestones : []
  const projectMap     = Object.fromEntries(safeProjects.map(p => [p.id, p]))
  const byProject      = Array.isArray(metrics?.by_project) ? metrics.by_project : []
  const byMember       = Array.isArray(metrics?.by_member) ? metrics.by_member : []
  const upcomingMilestones = Array.isArray(metrics?.upcoming_milestones) ? metrics.upcoming_milestones : []
  const engineersOut   = Array.isArray(metrics?.engineers_out) ? metrics.engineers_out : []
  const top6Projects   = [...byProject].sort((a, b) => b.total_hours - a.total_hours).slice(0, 6).map(p => ({ ...p, label: p.nickname || p.project_name }))

  const dateRangeActions = (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
      <Ico.Calendar />
      <input type="date" className="border-0 bg-transparent text-sm focus:outline-none w-[7.5rem]"
        value={startDate ?? ''} onChange={e => setStartDate(e.target.value)} />
      <span className="text-gray-300">→</span>
      <input type="date" className="border-0 bg-transparent text-sm focus:outline-none w-[7.5rem]"
        value={endDate ?? ''} onChange={e => setEndDate(e.target.value)} />
    </div>
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard.overview.title')}
        subtitle={t('dashboard.overview.subtitle')}
        actions={dateRangeActions}
      />

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : !metrics ? (
        <div className="text-gray-400 text-sm">{t('dashboard.overview.noData')}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCardModern
              icon={<Ico.Hours />} iconBg="bg-blue-50" iconColor="text-blue-600"
              label={t('dashboard.overview.totalHours')} value={(metrics.total_hours ?? 0).toFixed(0)}
              sub={t('dashboard.overview.allocatedInRange')}
            />
            <StatCardModern
              icon={<Ico.Users />} iconBg="bg-violet-50" iconColor="text-violet-600"
              label={t('dashboard.overview.engineers')} value={byMember.length}
              sub={t('dashboard.overview.withAllocations')}
            />
            <StatCardModern
              icon={<Ico.Status />} iconBg={(metrics.delayed_projects_count ?? 0) > 0 ? 'bg-red-50' : 'bg-emerald-50'}
              iconColor={(metrics.delayed_projects_count ?? 0) > 0 ? 'text-red-600' : 'text-emerald-600'}
              label={t('dashboard.overview.projectsDelayed')} value={metrics.delayed_projects_count ?? 0}
              sub={t('dashboard.overview.withOverdueMilestones')}
            />
            <StatCardModern
              icon={<Ico.Flag />} iconBg={upcomingMilestones.length > 0 ? 'bg-amber-50' : 'bg-emerald-50'}
              iconColor={upcomingMilestones.length > 0 ? 'text-amber-600' : 'text-emerald-600'}
              label={t('dashboard.overview.upcomingDeadlines')} value={upcomingMilestones.length}
              sub={t('dashboard.inNext14Days')}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('dashboard.overview.topProjectsByHours')}</h3>
              <p className="text-xs text-gray-400 mb-4">{t('dashboard.overview.top6Subtitle')}</p>
              {top6Projects.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">{t('dashboard.overview.noData2')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={top6Projects} layout="vertical" margin={{ left: 0, right: 24, top: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="label" width={90} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={v => [`${v}h`, t('dashboard.overview.hours')]} />
                    <Bar dataKey="total_hours" radius={[0, 4, 4, 0]}>
                      {top6Projects.map(p => <Cell key={p.project_id} fill={p.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('dashboard.overview.engineersOutThisWeek')}</h3>
              <p className="text-xs text-gray-400 mb-4">{t('dashboard.overview.ptoMedicalWorkTrips')}</p>
              {engineersOut.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <p className="text-sm">{t('dashboard.overview.everyoneIn')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {engineersOut.map((e, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${TYPE_DOT[e.type] || 'bg-gray-300'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{e.team_member_name}</p>
                        <p className="text-xs text-gray-400">{formatDate(e.start_date)} – {formatDate(e.end_date)}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${TYPE_COLORS[e.type] || 'bg-gray-100 text-gray-600'}`}>
                        {e.type === 'PTO' ? t('dashboard.timeOff.PTO') : e.type === 'medical' ? t('dashboard.timeOff.medical') : e.type === 'work_trip' ? t('dashboard.timeOff.workTrip') : e.type === 'admin' ? t('dashboard.timeOff.admin') : e.type}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('dashboard.overview.milestoneTimeline')}</h3>
            <p className="text-xs text-gray-400 mb-4">{t('dashboard.overview.allOpenMilestones')}</p>
            <MilestoneTimeline milestones={safeMilestones.filter(m => projectMap[m.project_id]?.is_active)} projectMap={projectMap} />
          </div>
        </>
      )}
    </div>
  )
}

// ── This Week tab ─────────────────────────────────────────────────────────────

function ThisWeekTab() {
  const { t } = useTranslation('engineering')
  const [week, setWeek]             = useState(null)
  const [projects, setProjects]     = useState([])
  const [members, setMembers]       = useState([])
  const [allocations, setAllocations] = useState([])
  const [loading, setLoading]       = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [sentWeeks, projs, mems] = await Promise.all([getSentWeeks(), getProjects(), getTeamMembers()])
        if (cancelled) return
        const sorted = Array.isArray(sentWeeks) ? [...sentWeeks].sort((a, b) => b.week_start.localeCompare(a.week_start)) : []
        const isSent = sorted.length > 0
        const resolvedWeek = isSent ? sorted[0].week_start : currentWeekMonday()
        setWeek(resolvedWeek)
        const allocs = isSent ? await getSentWeekAllocations(resolvedWeek) : await getAllocations({ week_start: resolvedWeek })
        if (cancelled) return
        setProjects(Array.isArray(projs) ? projs.filter(p => p.is_active && !p.is_purchase_only) : [])
        setMembers(Array.isArray(mems) ? mems : [])
        setAllocations(Array.isArray(allocs) ? allocs : [])
      } finally { if (!cancelled) setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const memberById = useMemo(() => {
    const m = new Map()
    for (const mem of members) m.set(mem.id, mem)
    return m
  }, [members])

  const projectCards = useMemo(() => {
    const byProject = new Map()
    for (const a of allocations) {
      if (!a.hours || a.hours <= 0) continue
      const list = byProject.get(a.project_id) || []
      list.push(a)
      byProject.set(a.project_id, list)
    }
    return projects
      .map(p => {
        const allocs = byProject.get(p.id) || []
        const engineers = allocs
          .map(a => ({ id: a.team_member_id, name: memberById.get(a.team_member_id)?.name || 'Unknown', hours: a.hours }))
          .sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name))
        return { project: p, engineers, totalHours: engineers.reduce((s, e) => s + e.hours, 0) }
      })
      .filter(c => c.engineers.length > 0)
      .sort((a, b) => b.totalHours - a.totalHours || a.project.name.localeCompare(b.project.name))
  }, [projects, allocations, memberById])

  if (loading) return <div className="text-sm text-gray-400">Loading…</div>

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard.thisWeek.title')}
        subtitle={week ? `${formatWeekRange(week)} · ${t('thisWeek.subtitle')}` : '—'}
      />
      {projectCards.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">{t('dashboard.thisWeek.noAllocations')}</p>
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {projectCards.map(({ project, engineers, totalHours }) => {
            const headerBg = project.color || '#6366f1'
            const light    = isLightHex(headerBg)
            return (
              <Link key={project.id} to={`/projects/${project.id}`}
                className="block bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-md transition-all">
                <div className="px-4 py-3 flex items-start justify-between gap-2" style={{ backgroundColor: headerBg }}>
                  <div className="min-w-0">
                    <h3 className={`text-sm font-semibold truncate ${light ? 'text-gray-900' : 'text-white'}`}>
                      {project.nickname || project.name}
                    </h3>
                    {project.code && <p className={`text-[11px] mt-0.5 truncate ${light ? 'text-gray-700' : 'text-white/80'}`}>{project.code}</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className={`text-sm font-semibold ${light ? 'text-gray-900' : 'text-white'}`}>{totalHours}h</p>
                    <p className={`text-[10px] uppercase tracking-wider ${light ? 'text-gray-600' : 'text-white/70'}`}>{t('thisWeek.total')}</p>
                  </div>
                </div>
                <div className="p-4">
                  {project.po_pm && <p className="text-[11px] text-gray-500 mb-2 truncate">{t('thisWeek.pmpo', {name: project.po_pm})}</p>}
                  <ul className="space-y-1.5">
                    {engineers.map(e => (
                      <li key={e.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-gray-700 truncate">{e.name}</span>
                        <span className="text-gray-500 tabular-nums shrink-0">{e.hours}h</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Root component ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth()
  const { t } = useTranslation('engineering')
  const [tab, setTab] = useState('my-week')

  const TABS = [
    { id: 'my-week',    label: t('dashboard.tabs.myWeek') },
    { id: 'overview',   label: t('dashboard.tabs.overview') },
    { id: 'this-week',  label: t('dashboard.tabs.thisWeek') },
  ]

  return (
    <div className="p-8">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === t.id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'my-week'   && <MyWeekTab userName={user?.display_name} teamMemberId={user?.team_member_id ?? null} />}
      {tab === 'overview'  && <OverviewTab />}
      {tab === 'this-week' && <ThisWeekTab />}
    </div>
  )
}
